import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { welcomeRuleService } from '@/services/welcomeRuleService';
import type { WelcomeStatus, WelcomeHistoryEntry } from '@/types/welcome';
import { Check, X, RefreshCw } from 'lucide-react';

interface Props {
  clientId: number;
  onOpenFullHistory: () => void;
}

const STATUS_BADGE: Record<WelcomeStatus, { label: string; className: string }> = {
  SENT: { label: 'Enviado', className: 'bg-emerald-100 text-emerald-700' },
  FAILED: { label: 'Falha', className: 'bg-red-100 text-red-700' },
  IGNORED: { label: 'Ignorado', className: 'bg-gray-100 text-gray-700' },
};

// Entrega real (Meta, via relay cleoia). "Enviado" = aceito pela Meta;
// "Entregue"/"Lido" = chegou/abriu; "Falhou" = não entregue (ver erro).
const DELIVERY_BADGE: Record<string, { label: string; className: string }> = {
  delivered: { label: 'Entregue', className: 'bg-emerald-100 text-emerald-700' },
  read: { label: 'Lido', className: 'bg-sky-100 text-sky-700' },
  sent: { label: 'Aceito Meta', className: 'bg-gray-100 text-gray-600' },
  failed: { label: 'Falhou', className: 'bg-red-100 text-red-700' },
};

// Motivo da falha em PT (com código e categoria) p/ tooltip/legenda.
const deliveryFailTooltip = (d: {
  delivery_error_pt?: string | null;
  delivery_error_title?: string | null;
  delivery_error_code?: number | null;
  delivery_error_categoria_label?: string | null;
}): string => {
  const base = d.delivery_error_pt || d.delivery_error_title || 'Falha na entrega';
  const code = d.delivery_error_code ? ` (cód ${d.delivery_error_code})` : '';
  const cat = d.delivery_error_categoria_label ? ` · ${d.delivery_error_categoria_label}` : '';
  const meta = d.delivery_error_title && d.delivery_error_title !== base ? ` · Meta: ${d.delivery_error_title}` : '';
  return `${base}${code}${cat}${meta}`;
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

export const WelcomeHistoryTable: React.FC<Props> = ({ clientId, onOpenFullHistory }) => {
  const [status, setStatus] = useState<WelcomeStatus | 'ALL'>('ALL');
  const [data, setData] = useState<WelcomeHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const r = await welcomeRuleService.listHistory(clientId, {
        since,
        limit: 50,
        status: status === 'ALL' ? undefined : status,
      });
      setData(r.data || []);
      setTotal(r.total || 0);
    } catch (e) {
      console.error('[Welcome] erro ao carregar histórico:', e);
    } finally {
      setLoading(false);
    }
  }, [clientId, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Histórico — últimos 7 dias</CardTitle>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={(v) => setStatus(v as WelcomeStatus | 'ALL')}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="SENT">Enviados</SelectItem>
              <SelectItem value="FAILED">Falhas</SelectItem>
              <SelectItem value="IGNORED">Ignorados</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Recarregar
          </Button>
          <Button variant="outline" size="sm" onClick={onOpenFullHistory}>
            Histórico
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500">
              <tr>
                <th className="py-2">Data/hora</th>
                <th>Associado</th>
                <th>Telefone</th>
                <th>Veículo</th>
                <th>Status</th>
                <th>Entrega</th>
                <th>Situação</th>
                <th>Erro</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="py-4 text-center text-gray-400">
                    Sem registros nos últimos 7 dias.
                  </td>
                </tr>
              )}
              {data.map((row) => {
                const badge = STATUS_BADGE[row.status];
                return (
                  <tr key={row.id} className="border-t">
                    <td className="py-2">{formatDateTime(row.executed_at)}</td>
                    <td>
                      {row.associado_nome || '—'}{' '}
                      <span className="text-gray-400">({row.codigo_associado})</span>
                    </td>
                    <td>{row.telefone || '—'}</td>
                    <td>
                      {row.placa || '—'}{' '}
                      <span className="text-gray-400">({row.codigo_veiculo ?? '—'})</span>
                    </td>
                    <td><Badge className={badge.className}>{badge.label}</Badge></td>
                    <td>
                      {row.delivery_status ? (
                        <div className="flex flex-col gap-0.5">
                          <Badge
                            className={(DELIVERY_BADGE[row.delivery_status] || DELIVERY_BADGE.sent).className}
                            title={row.delivery_status === 'failed' ? deliveryFailTooltip(row) : ''}
                          >
                            {(DELIVERY_BADGE[row.delivery_status] || { label: row.delivery_status }).label}
                          </Badge>
                          {row.delivery_status === 'failed' && (row.delivery_error_pt || row.delivery_error_title) && (
                            <span className="text-[11px] leading-tight text-red-600">
                              {row.delivery_error_pt || row.delivery_error_title}
                              {row.delivery_error_categoria_label ? ` · ${row.delivery_error_categoria_label}` : ''}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td>
                      {row.status === 'SENT' &&
                        (row.situacao_atualizada ? (
                          <Check className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <X className="w-4 h-4 text-amber-600" />
                        ))}
                    </td>
                    <td className="max-w-xs truncate" title={row.error_message || ''}>
                      {row.error_message || ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-xs text-gray-500">Total: {total}</div>
      </CardContent>
    </Card>
  );
};

export default WelcomeHistoryTable;
